/**
 * The changelog, newest first, one page at a time (#1355). The startup check
 * and the dialog share this query, so opening the dialog after the check
 * costs no second request.
 */

import { useInfiniteQuery } from '@tanstack/react-query'
import { changelogService } from '@/services/changelog-service'

export const CHANGELOG_PAGE_SIZE = 50

export const changelogKeys = {
  list: ['changelog', 'list'] as const,
}

/** Where the next page starts: the last entry received, its date and key. */
export interface ChangelogCursor {
  before: string
  beforeKey: string
}

export function useChangelog(enabled = true) {
  const query = useInfiniteQuery({
    queryKey: changelogKeys.list,
    queryFn: ({ pageParam }) =>
      changelogService.list({ limit: CHANGELOG_PAGE_SIZE, ...(pageParam ?? {}) }),
    initialPageParam: null as ChangelogCursor | null,
    // The cursor is the server's string as sent (microseconds). A round trip
    // through Date would cut it to milliseconds and skip rows at the seam.
    // The key breaks ties: several entries can share one date (#1456).
    getNextPageParam: (lastPage): ChangelogCursor | undefined => {
      const last = lastPage.entries[lastPage.entries.length - 1]
      return lastPage.hasMore && last ? { before: last.publishedAt, beforeKey: last.key } : undefined
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
  })
  const entries = query.data?.pages.flatMap((page) => page.entries) ?? []
  return { ...query, entries }
}
